import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { disableYandexMetrika, enableYandexMetrika } from '../services/yandexMetrika';

// Расширяем интерфейс Window для аналитики
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    ym?: (id: number, method: string, ...args: any[]) => void;
  }
}

interface CookieConsentProps {
  className?: string;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Проверяем, дал ли пользователь согласие ранее
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Показываем баннер через небольшую задержку для лучшего UX
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 2000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    // Включаем аналитику и трекинг
    enableAnalytics();
    hideBanner();
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    // Отключаем аналитику и очищаем куки
    disableAnalytics();
    clearNonEssentialCookies();
    hideBanner();
  };

  const enableAnalytics = async () => {
    // Включение Google Analytics, Яндекс.Метрики и других трекеров
    if (typeof window !== 'undefined') {
      try {
        // Google Analytics
        if (window.gtag) {
          window.gtag('consent', 'update', {
            analytics_storage: 'granted',
            ad_storage: 'granted'
          });
        }
        
        // Яндекс.Метрика - инициализируем
        await enableYandexMetrika();
        
        console.log('✅ Аналитика включена');
      } catch (error) {
        console.error('Ошибка включения аналитики:', error);
      }
    }
  };

  const disableAnalytics = () => {
    // Отключение всех трекеров и аналитики
    if (typeof window !== 'undefined') {
      try {
        // Google Analytics
        if (window.gtag) {
          window.gtag('consent', 'update', {
            analytics_storage: 'denied',
            ad_storage: 'denied'
          });
        }
        
        // Яндекс.Метрика - отключаем
        disableYandexMetrika();
        
        console.log('❌ Аналитика отключена');
      } catch (error) {
        console.error('Ошибка отключения аналитики:', error);
      }
    }
  };

  const clearNonEssentialCookies = () => {
    // Удаляем все несущественные куки
    const cookies = document.cookie.split(';');
    
    // Список технически необходимых куки (не удаляем)
    const essentialCookies = [
      'cookie-consent',
      'cookie-consent-date',
      'auth-token',
      'session-id'
    ];
    
    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      
      // Удаляем только несущественные куки
      if (!essentialCookies.some(essential => cookieName.includes(essential))) {
        // Удаляем куки для разных доменов и путей
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
      }
    });
    
    console.log('🧹 Несущественные куки удалены');
  };

  const hideBanner = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div className={`cookie-consent ${isAnimating ? 'visible' : ''} ${className}`}>
      <div className="cookie-consent-container">
        <div className="cookie-consent-content">
          <div className="cookie-icon">
            <Cookie size={24} />
          </div>
          <div className="cookie-text">
            <p>
              Мы используем куки для улучшения работы сайта и персонализации контента. 
              Продолжая пользоваться сайтом, вы соглашаетесь с нашей{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                политикой конфиденциальности
              </a>.
            </p>
          </div>
        </div>
        
        <div className="cookie-actions">
          <button 
            className="btn-cookie-decline"
            onClick={handleDecline}
            aria-label="Отклонить куки"
          >
            Отклонить
          </button>
          <button 
            className="btn-cookie-accept"
            onClick={handleAccept}
            aria-label="Принять куки"
          >
            Принять
          </button>
        </div>
        
        <button 
          className="cookie-close"
          onClick={handleDecline}
          aria-label="Закрыть баннер куки"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
